import { WorkflowManager } from "@archipad/backend/project/workflowManager";
import { createEntityWithProperties, fetchEntityByTemplateName } from "@archipad-js/core/orm";
import { BugNoteFacade } from "./bugNote.facade";
import { IllegalStateError } from "@archipad-js/core/error";
import { voidAbortSignal, voidProgress } from "@archipad-js/core/async";
import { prepareAttachment } from "@archipad/backend/archiweb/synchroDriver";
import { AnnotatedEntityDelegate } from "@archipad/delegate/annotatedEntityDelegate";
import { Data } from "@core/services/file/file";
import { firstValueFrom } from "rxjs";
import { AnnotationService, BugService } from "@archipad-js/archipad/entity-services";
import { PatchesDescriptor, PatchDescriptor } from "./file/patchFile";
import { WorkflowService } from "@archipad-js/archipad/entity-services";
import { BugNoteType } from "@archipad/backend/project/bugNoteConstant";
import { ProjectEntity, BugEntity, BugNoteEntity, BugStateEntity, VisitEntity, PersonEntity, ProjectUserEntity } from "@archipad-js/archipad-model";

export const SIGN_ATTACHMENT_KEY = "signature.jpg";

export class SignOffService {
    constructor(
        protected readonly workflowManager: WorkflowManager,
        protected readonly bugService: BugService,
        protected readonly bugNoteFacade: BugNoteFacade,
        protected readonly workflowService: WorkflowService = new WorkflowService(),
    ){ }

    canApproveOrRefuseSignOff(project: ProjectEntity, bug: BugEntity): {
        canApprove: boolean,
        canRefuse: boolean,
        canRecordSignOff: boolean,
    } {
        if(!bug || !project){
            return {
                canApprove: false,
                canRefuse: false,
                canRecordSignOff: false,
            }
        }
        const isOriginalDeliveryGroup = bug.bugGroup?.templateName === 'deliveryGroup';
        bug = bug.relatedParentBug ?? bug; // Ensure we are working with the parent bug if it exists
        const actualBugState = this.workflowManager.findBugState(bug);
        
        const signOffRejectedBugState = fetchEntityByTemplateName(bug.getContext(), 'BugState', 'deliveryGroup/signOff_rejected');
        const rejectTransition = this.bugService.findExplicitSystemTransition(actualBugState, signOffRejectedBugState, project);

        const signOffApprovedBugState = fetchEntityByTemplateName(bug.getContext(), 'BugState', 'deliveryGroup/signOff_approved');
        const approveTransition = this.bugService.findExplicitSystemTransition(actualBugState, signOffApprovedBugState, project);

        return {
            canApprove: !!approveTransition,
            canRefuse: isOriginalDeliveryGroup && !!rejectTransition,
            canRecordSignOff: isOriginalDeliveryGroup && !!approveTransition,
        }
    }

    refuseSignOff(bug: BugEntity, visit: VisitEntity): void {
        const project = bug.project;
        const actualBugState = this.workflowManager.findBugState(bug);
        const finalBugState = fetchEntityByTemplateName(bug.getContext(), 'BugState', 'deliveryGroup/signOff_rejected');
        const systemTransition = this.bugService.findExplicitSystemTransition(actualBugState, finalBugState, project);
        
        if (!systemTransition) {
            throw new IllegalStateError("Cannot refuse sign-off: no system transition found");
        }

        const bugNote = this.bugNoteFacade.createBugNote(bug, visit);
        this.workflowManager.executeTransition(systemTransition, bugNote, bug, visit);
    }
    
    isApprovedSignOff(bug: BugEntity): boolean {
        bug = bug.relatedParentBug ?? bug; // Ensure we are working with the parent bug if it exists
        const actualBugState = this.workflowManager.findBugState(bug);
        const isApproved = actualBugState.templateName === 'deliveryGroup/signOff_approved';
        return isApproved;
    }

    /**
     * The state a bug was in before its signed sign-off: the state of its last note before the notes that approved the
     * sign-off. Null when the bug is not in the approved sign-off state, or has no state before it.
     */
    findStateBeforeApprovedSignOff(bug: BugEntity): BugStateEntity | null {
        if (!this.isApprovedSignOff(bug)) {
            return null;
        }
        bug = bug.relatedParentBug ?? bug;
        const previousNote = bug.notes
            .filter((note) => note.bugState && !note.temp)
            .sort((a, b) => (b.creationDate?.getTime() ?? 0) - (a.creationDate?.getTime() ?? 0))
            .find((note) => note.bugState.templateName !== 'deliveryGroup/signOff_approved');
        return previousNote?.bugState ?? null;
    }

    /**
     * Cancels the signed sign-off of a bug (the support, from the debug menu): a new note of the current user puts it
     * back in the state it was in before. The sign-off and its note stay in the history.
     */
    cancelApprovedSignOff(bug: BugEntity): BugNoteEntity {
        const previousState = this.findStateBeforeApprovedSignOff(bug);
        if (!previousState) {
            throw new IllegalStateError("Cannot cancel the sign-off: the bug is not in the approved sign-off state, or has no state before it");
        }
        bug = bug.relatedParentBug ?? bug;
        const bugNote = this.workflowManager.restoreBugState(previousState, bug);
        bugNote.text = l('Sign-off cancelled');
        return bugNote;
    }

    async approveSignOff(bug: BugEntity, visit: VisitEntity, user: ProjectUserEntity, payload: {
        occupant: {
            signatureData: Blob,
            person?: PersonEntity,
            signAt?: Date,
            context?: string,
        }
        company?: {
            signatureData: Blob,
            person: PersonEntity,
            signAt: Date,
            context: string,
        },
    }): Promise<void> {
        const project = bug.project;
        const context = project.getContext();
        const actualBugState = this.workflowManager.findBugState(bug);
        const finalBugState = fetchEntityByTemplateName(context, 'BugState', 'deliveryGroup/signOff_approved');
        const systemTransition = this.bugService.findExplicitSystemTransition(actualBugState, finalBugState, project);
        if (!systemTransition) {
            throw new IllegalStateError("Cannot approve sign-off: no system transition found");
        }

        const occupantSignature = createEntityWithProperties(context, 'Signature', {
            contact: payload.occupant.person,
            context: payload.occupant.context,
            creationDate: payload.occupant.signAt,
        });
        const bugNote = this.bugNoteFacade.createBugNote(bug, visit);
        this.workflowManager.executeTransition(systemTransition, bugNote, bug, visit);
        bugNote.text = payload.occupant.context || bugNote.text;
        bugNote.creationDate = payload.occupant.signAt ?? bugNote.creationDate;
        bugNote.date = payload.occupant.signAt ?? bugNote.date;
        bugNote.user = user;
        bugNote.type = BugNoteType.TYPE_HISTORY_SIGNOFF;
        const signOff = createEntityWithProperties(context, 'SignOff', {
            bug: bug,
            occupantSignature: occupantSignature,
            companySignature: null, // will be set later if company signature is provided
            bugNote: bugNote,
        });
        const annotatedEntityDelegate = new AnnotatedEntityDelegate(
            null,
            new AnnotationService(),
        );
        annotatedEntityDelegate.setAnnotatedEntity(bugNote);
        const file = new File([payload.occupant.signatureData], SIGN_ATTACHMENT_KEY, { type: payload.occupant?.signatureData?.type });
        await annotatedEntityDelegate.setAttachment(voidAbortSignal(), voidProgress(), file, project.id);
        

        const tempDir = new PatchesDescriptor(project.id, "temp");
        const tempPatch = new PatchDescriptor(tempDir, "current");
        const occupantAttachmentSignature = await firstValueFrom(prepareAttachment(voidProgress(), tempPatch, Data.fromBlob(payload.occupant.signatureData), occupantSignature.id, SIGN_ATTACHMENT_KEY));
        occupantSignature.setAttachmentForKey(SIGN_ATTACHMENT_KEY, occupantAttachmentSignature);
        if (payload.company && payload.company.signatureData) {
            const companySignature = createEntityWithProperties(context, 'Signature', {
                contact: payload.company.person,
                context: payload.company.context,
                creationDate: payload.company.signAt,
            });
            signOff.companySignature = companySignature;
            const companyAttachmentSignature = await firstValueFrom(prepareAttachment(voidProgress(), tempPatch, Data.fromBlob(payload.company.signatureData), companySignature.id, SIGN_ATTACHMENT_KEY));
            companySignature.setAttachmentForKey(SIGN_ATTACHMENT_KEY, companyAttachmentSignature);
        }
        if (bug.relatedChildBugs.length){
            for (const childBug of bug.relatedChildBugs) {
                const artisanBugNote = createEntityWithProperties(childBug.getContext(), 'BugNote', {
                    text: bugNote.text,
                    creationDate: bugNote.creationDate,
                    date: bugNote.date,
                    bug: childBug,
                    visit: visit,
                    signOff: signOff,
                    user: user,
                    type: BugNoteType.TYPE_HISTORY_SIGNOFF,
                });
                annotatedEntityDelegate.setAnnotatedEntity(artisanBugNote);
                await annotatedEntityDelegate.setAttachment(voidAbortSignal(), voidProgress(), file, project.id);
            }
        }
    }

    recordSignOff(bug: BugEntity, user: ProjectUserEntity, visit?: VisitEntity): void {
        const project = bug.project;
        const context = project.getContext();
        const actualBugState = this.workflowManager.findBugState(bug);
        const finalBugState = fetchEntityByTemplateName(context, 'BugState', 'deliveryGroup/signOff_approved');
        const systemTransition = this.bugService.findExplicitSystemTransition(actualBugState, finalBugState, project);
        if (!systemTransition) {
            throw new IllegalStateError("Cannot approve sign-off: no system transition found");
        }
        const bugNote = this.bugNoteFacade.createBugNote(bug, visit);
        bugNote.text = bugNote.text || l('Sign-off recorded');
        this.workflowManager.executeTransition(systemTransition, bugNote, bug, visit);
    }
}